import React, {useMemo} from 'react';
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/system/useMediaQuery";
import {Box, CircularProgress, Typography, useTheme} from "@mui/joy";
import {useMutation, useQuery} from "@apollo/client";
import _ from "lodash";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {ContactsRounded, LinkRounded} from "@mui-symbols-material/w300";
import {BasicModalContent, TDAForm} from "@dreebit-registry/vsm-microservice-components";
import {ContactPersonCreateTdas} from "../../graphql/queries/contactPersonCreateTdas";
import {ContactPersonUpdateTdas} from "../../graphql/queries/contactPersonUpdateTdas";
import {CreateContactPerson} from "../../graphql/mutations/createContactPerson";
import {UpdateContactPerson} from "../../graphql/mutations/updateContactPerson";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {prepareContactPersonAttributes} from "../../utils/contactPersonFormAttributes";
import {contactPersonInputRenderers, getLinkedSourceHint} from "./contactPersonInputRenderers";

interface Props {
    mode: 'create' | 'update';
    // create: the owning client of the new contact (the node owner's client)
    clientId?: string;
    // update: the person being edited
    contactPersonId?: string;
    // update: OWN | USER | SERVICE_PROVIDER_CONTACT — anything but OWN means the
    // person's name/email/phone resolve from a source and render read-only
    sourceKind?: string | null;
    open: boolean;
    onClose: () => void;
    // create passes the new person's id so the caller can auto-assign it
    onSuccess?: (resultData: any) => void;
    refetchQueries?: string[];
}

/**
 * TDA-driven create/edit form for a contact person, embedded in the node contact
 * tab. Create uses the list-level CONTACT_PERSON_CREATE metadata scoped to the
 * owning client; update uses the per-entity metadata on `contactPerson(id)`,
 * which arrives prefilled. The free-text role shows the backend's proposals as
 * chips; on a linked contact the resolved fields are read-only (the update
 * ignores them server-side) and only the role can change.
 */
const ContactPersonFormModal = ({mode, clientId, contactPersonId, sourceKind, open, onClose, onSuccess, refetchQueries}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isCreate = mode === 'create';
    const isLinked = !isCreate && !!sourceKind && sourceKind !== 'OWN';

    const attributesQueryResult = useQuery(
        isCreate ? ContactPersonCreateTdas : ContactPersonUpdateTdas,
        {
            variables: isCreate ? {clientId} : {contactPersonId},
            fetchPolicy: "network-only",
        }
    );

    const attributesPath = isCreate
        ? 'contactPersonList.typeDefinitionAttributeList.typeDefinitionAttributes'
        : 'contactPerson.typeDefinitionAttributeList.typeDefinitionAttributes';
    const tdaAttributes = _.get(attributesQueryResult, `data.${attributesPath}`, []);
    const attributes = useMemo(() => prepareContactPersonAttributes(tdaAttributes), [tdaAttributes]);
    const inputRenderers = useMemo(() => contactPersonInputRenderers(), []);

    const [mutationFunction, {loading: mutationLoading}] = useMutation(
        isCreate ? CreateContactPerson : UpdateContactPerson
    );

    const handleSubmit = (values: any) => {
        const input = {...values};
        // optional fields: never persist empty strings
        ['email', 'phone', 'role'].forEach((key) => {
            if (input[key] === '') input[key] = null;
        });

        return mutationFunction({
            variables: isCreate ? {clientId, input} : {id: contactPersonId, input},
            refetchQueries: refetchQueries || [],
        })
            .then(res => {
                if (res.data && onSuccess) {
                    onSuccess(res.data);
                }
            })
            .catch(err => {
                toast.error(resolveErrorMessage(err, t));
            });
    };

    return (
        <Dialog
            open={open}
            onClose={mutationLoading ? undefined : onClose}
            fullScreen={isFullScreen}
            slotProps={{
                paper: {sx: {minWidth: isFullScreen ? undefined : "600px"}},
            }}
        >
            <BasicModalContent
                startIcon={<ContactsRounded/>}
                onClose={mutationLoading ? undefined : onClose}
                title={
                    <Typography level="titleSmallLight">
                        {isCreate ? t('Add contact') : t('Edit contact')}
                    </Typography>
                }
            >
                {attributesQueryResult.loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress variant="soft"/>
                    </Box>
                ) : (
                    <>
                        {isLinked && (
                            <Typography
                                level="body-sm"
                                startDecorator={<LinkRounded sx={{fontSize: 16}}/>}
                                sx={{color: 'text.secondary', mb: '4px'}}
                                data-testid="contact-linked-hint"
                            >
                                {getLinkedSourceHint(t, sourceKind)} {t('Only the role can be changed here.')}
                            </Typography>
                        )}
                        <TDAForm
                            attributes={attributes}
                            customInputRenderers={inputRenderers}
                            onSubmit={handleSubmit}
                            submitButtonText={isCreate ? t('Create') : t('Save')}
                        />
                    </>
                )}
            </BasicModalContent>
        </Dialog>
    );
};

export default ContactPersonFormModal;
